import {
  CollectiveAdvice,
  NeedName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";
import {
  AdviceCoverageCard,
  AdviceNeedCard,
  AkaAdviceScreenshotData,
  NeedAdviceSummary,
} from "./interfaces";

import { VALUE_TYPE } from "Components/NumberValue";
import { removeWhitespaceOptional } from "Formatting/index";
import { isDefined } from "Utils/functions";
import { AkaAdvicePriceFormValues } from "../AkaAdviceCards/interfaces";

const PRICE_VALUE = "value-element__value";

export const generateIndividualAdviceScreenshot = (
  adviceValues: CollectiveAdvice,
  advicePriceFormValues?: AkaAdvicePriceFormValues | undefined,
  ref?: HTMLDivElement | null,
): AkaAdviceScreenshotData | null => {
  if (!isDefined(ref) || !isDefined(advicePriceFormValues)) {
    return null;
  }
  return {
    adviceResponse: adviceValues,
    adviceCharts: {},
    advicePriceFormValues,
    adviceIntroTexts: extractTextsByClassOrTagName("AdviceIntroText", ref),
    needAdviceSummaries: extractAkaAdviceIntroMessages(ref),
    adviceNeedCards: extractAkaAdviceCardsData(ref),
  };
};

const extractTextsByClassOrTagName = (
  name: string,
  ref?: HTMLDivElement | Element,
  tag = false,
): string[] => {
  if (!ref) {
    return [];
  }
  const list = tag
    ? ref.getElementsByTagName(name)
    : ref.getElementsByClassName(name);
  if (list) {
    return Array.from(list)
      .map((element) => element.textContent)
      .filter((text): text is string => !!text);
  }
  return [];
};

const extractAkaAdviceIntroMessages = (
  ref: HTMLDivElement,
): NeedAdviceSummary[] => {
  const adviceIntroMessages = getFirst(
    "AdviceIntroMessages",
    ref,
  )?.getElementsByTagName("div");
  return Array.from(adviceIntroMessages ?? []).map((message) => ({
    title: getTextOrUndefined("AdviceMessageTitle", message),
    coverages: [],
    text: getTextOrUndefined("AdviceMessageText", message),
    warning: getTextOrUndefined("AdviceMessageWarn", message),
  }));
};

const extractAkaAdviceCardsData = (ref: HTMLDivElement): AdviceNeedCard[] => {
  return [
    ...mapAkaAdviceCard(NeedName.LIFE, getFirst("AdviceCard_DEATH", ref)),
    ...mapAkaAdviceCard(
      NeedName.DISABILITY,
      getFirst("AdviceCard_DISABILITY_FLEX", ref),
    ),
    ...mapAkaAdviceCard(
      NeedName.CRITICAL_ILLNESS,
      getFirst("AdviceCard_CRITICAL_ILLNESS", ref),
    ),
  ];
};

export const mapAkaAdviceCard = (
  insurance: NeedName,
  adviceCard?: Element,
): AdviceNeedCard[] => {
  if (!adviceCard) {
    return [];
  }

  const adviceCardContent = getFirst("AdviceCardContent", adviceCard);
  const adviceHeaderText = getTextOrUndefined(
    "AdviceHeaderText",
    adviceCardContent,
  );
  const adviceTextsReason = extractTextsByClassOrTagName(
    "AdviceTextsReason",
    adviceCardContent,
  );
  const adviceTextReason = adviceTextsReason
    .join(" ")
    .replace(`\u2009${VALUE_TYPE.NOK}`, `\u2009${VALUE_TYPE.NOK}.`);

  const adviceTexts = extractTextsByClassOrTagName(
    "AdviceTexts",
    adviceCardContent,
  );

  const effectivePaymentInfo = getTextOrUndefined(
    "div",
    getFirst("EffectivePaymentInfo", adviceCardContent),
    true,
  );
  const purchaseFromAdvisorText = getTextOrUndefined(
    "div",
    getFirst("PurchaseFromAdvisorText", adviceCardContent),
    true,
  );

  const coverageCards: AdviceCoverageCard[] = [];
  if (adviceCardContent) {
    coverageCards.push(...extractIndividualCoverageCard(adviceCard));
    if (insurance === NeedName.DISABILITY) {
      coverageCards.push(...extractCustomerOneTimePayment(adviceCardContent));
      coverageCards.push(...extractAdvisorOneTimePayment(adviceCardContent));
    }
  }

  return [
    {
      name: insurance,
      content: {
        adviceHeaderText,
        adviceTexts: [adviceTextReason, ...adviceTexts],
        effectivePaymentInfo,
        purchaseFromAdvisorText,
      },
      coverageCards,
    },
  ];
};

const extractIndividualCoverageCard = (
  adviceCard: Element,
): AdviceCoverageCard[] => {
  const checkbox = getFirst("InsuranceCheckbox", adviceCard);
  const input = getFirst<HTMLInputElement>(
    'input[type="checkbox"]',
    checkbox,
    true,
  );
  const checked = Boolean(input?.checked);

  const insuranceName =
    getFirst("label", checkbox, true)?.lastElementChild?.textContent ??
    undefined;

  const insurancePriceWrapper = getFirst("AkaAdviceInsurancePrice", adviceCard);
  const insurancePrice = getTextOrUndefined(PRICE_VALUE, insurancePriceWrapper);
  const monthlyPrice =
    Number(removeWhitespaceOptional(insurancePrice)) ?? undefined;

  if (isDefined(insuranceName) && checked) {
    return [{ header: { name: insuranceName, monthlyPrice } }];
  }
  return [];
};

const extractCustomerOneTimePayment = (
  adviceCardContent: Element,
): AdviceCoverageCard[] => {
  const adviceForms = adviceCardContent.getElementsByClassName(
    "AkaAdviceInsuranceForm",
  );
  return Array.from(adviceForms ?? [])
    .filter((element) => {
      const insuranceName = getFirst("label", element, true)?.lastElementChild
        ?.textContent;
      return isDefined(insuranceName);
    })
    .map((element) => {
      const priceText = getTextOrUndefined(PRICE_VALUE, element);
      const monthlyPrice = Number(removeWhitespaceOptional(priceText ?? "0"));
      return { header: { name: "Engangsutbetaling", monthlyPrice } };
    });
};

const extractAdvisorOneTimePayment = (
  adviceCardContent: Element,
): AdviceCoverageCard[] => {
  const disabilityOneTimePaymentAdvisorForm = getFirst(
    "DisabilityOneTimePaymentAdvisorForm",
    adviceCardContent,
  );
  const insuranceName = getTextOrUndefined(
    "label",
    disabilityOneTimePaymentAdvisorForm,
    true,
  );
  const insurancePrice = getTextOrUndefined(
    PRICE_VALUE,
    disabilityOneTimePaymentAdvisorForm,
  );
  const monthlyPrice =
    Number(removeWhitespaceOptional(insurancePrice)) ?? undefined;
  if (isDefined(insuranceName)) {
    return [{ header: { name: insuranceName, monthlyPrice } }];
  }
  return [];
};

const getFirst = <T extends Element>(
  name: string,
  e: Element | undefined,
  tag = false,
): T | undefined => {
  if (!isDefined(e)) {
    return undefined;
  }

  const element = e.querySelector<T>(tag ? name : `.${name}`);
  return isDefined(element) ? element : undefined;
};

const getTextOrUndefined = (
  name: string,
  e: Element | undefined,
  tag = false,
): string | undefined => getFirst(name, e, tag)?.textContent ?? undefined;
